import React, { useEffect, useRef, useState } from 'react';
import { api } from '../api/client';

const CATEGORIES: Record<string, string> = {
  building: 'Барилга', elevator: 'Цахилгаан шат', parking: 'Зогсоол',
  playground: 'Тоглоомын талбай', security: 'Аюулгүй байдал', utility: 'Инженерийн шугам',
  furniture: 'Тавилга', other: 'Бусад',
};

const STATUSES: Record<string, string> = {
  active: 'Идэвхтэй', maintenance: 'Засварт', retired: 'Хуучирсан', disposed: 'Устгасан',
};

const API_ORIGIN = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1').replace(/\/api\/v1\/?$/, '');
const fmtDdtd = (d: string) => (d || '').replace(/(\d{4})(?=\d)/g, '$1 ').trim();
const MAX_FILES = 5;

const STATUS_COLORS: Record<string, string> = {
  active: '#236844', maintenance: '#f59e0b', retired: '#6b7280', disposed: '#ef4444',
};

interface AssetForm {
  name: string;
  category: string;
  building?: string;
  location: string;
  description: string;
  purchase_date: string;
  purchase_cost: number | '';
  current_value: number | '';
  serial_number: string;
  warranty_expires: string;
  next_maintenance: string;
  ebarimt_ddtd: string;
}

const emptyForm: AssetForm = {
  name: '', category: 'building', building: '', location: '', description: '',
  purchase_date: '', purchase_cost: '', current_value: '',
  serial_number: '', warranty_expires: '', next_maintenance: '', ebarimt_ddtd: '',
};

interface Summary {
  total_count: number;
  total_value: number;
  active_count: number;
  maintenance_due: number;
}

export default function AssetsPage() {
  const [assets, setAssets] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<AssetForm>(emptyForm);
  const [buildings, setBuildings] = useState<string[]>([]);
  const [files, setFiles] = useState<File[]>([]);              // хадгалахад хуулах шинэ баримтууд
  const [editFiles, setEditFiles] = useState<any[]>([]);       // засварлаж буй хөрөнгийн одоогийн баримтууд
  const [formErr, setFormErr] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  useEffect(() => { api.get<any>('/admin/buildings').then(r => setBuildings((r.items || []).map((b: any) => b.name as string))).catch(() => {}); }, []);
  const [summary, setSummary] = useState<Summary>({ total_count: 0, total_value: 0, active_count: 0, maintenance_due: 0 });

  const loadSummary = () => {
    api.get('/admin/assets/summary')
      .then((data: any) => setSummary(data))
      .catch(() => {});
  };

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (categoryFilter) params.set('category', categoryFilter);
    if (statusFilter) params.set('status', statusFilter);
    const qs = params.toString() ? `?${params.toString()}` : '';
    api.get(`/admin/assets${qs}`)
      .then((data: any) => { setAssets(data?.items || []); setTotal(data?.total || 0); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadSummary(); }, []);
  useEffect(() => { load(); }, [categoryFilter, statusFilter]);

  const pickFiles = (list: FileList | null) => {
    if (!list) return;
    const room = MAX_FILES - editFiles.length - files.length;
    const chosen = Array.from(list).filter(f => f.type.startsWith('image/') || f.type === 'application/pdf').slice(0, Math.max(0, room));
    if (Array.from(list).length > room) setFormErr(`Хамгийн ихдээ ${MAX_FILES} баримт (үлдсэн ${Math.max(0, room)})`); else setFormErr('');
    setFiles(f => [...f, ...chosen]);
    if (fileRef.current) fileRef.current.value = '';
  };

  const removeSavedFile = async (fileId: string) => {
    if (!editId) return;
    try { const r: any = await api.delete(`/admin/assets/${editId}/files/${fileId}`); setEditFiles(r.files || []); load(); }
    catch { setFormErr('Баримт устгаж чадсангүй'); }
  };

  const handleSubmit = async () => {
    const dd = (form.ebarimt_ddtd || '').replace(/\D/g, '');
    if (dd && dd.length !== 33) { setFormErr('ДДТД 33 оронтой тоо байх ёстой (е-баримт дээрх дугаар)'); return; }
    setUploading(true); setFormErr('');
    try {
      const body = {
        ...form,
        ebarimt_ddtd: dd,
        purchase_cost: form.purchase_cost === '' ? null : Number(form.purchase_cost),
        current_value: form.current_value === '' ? null : Number(form.current_value),
        purchase_date: form.purchase_date || null,
        warranty_expires: form.warranty_expires || null,
        next_maintenance: form.next_maintenance || null,
      };
      const saved: any = editId ? await api.put(`/admin/assets/${editId}`, body) : await api.post('/admin/assets', body);
      if (files.length) {
        const fd = new FormData(); files.forEach(f => fd.append('files', f));
        const res = await fetch(`${API_ORIGIN}/api/v1/admin/assets/${saved.id}/files`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${localStorage.getItem('admin_token') || ''}`, ...(localStorage.getItem('platform_tenant_id') ? { 'X-Tenant-Id': localStorage.getItem('platform_tenant_id') as string } : {}) },
          body: fd,
        });
        const d = await res.json();
        if (!res.ok) throw new Error(d.detail || 'Баримт хуулахад алдаа');
        if (d.ebarimt_ddtd && !dd) window.alert(`Баримтын зургаас ДДТД уншигдлаа: ${fmtDdtd(d.ebarimt_ddtd)}`);
      }
      setShowForm(false);
      setEditId(null);
      setForm(emptyForm);
      setFiles([]); setEditFiles([]);
      load();
      loadSummary();
    } catch (e: any) { setFormErr(e?.message ? String(e.message).replace(/^\{.*"detail":"/, '').replace(/".*$/, '') : 'Хадгалахад алдаа'); }
    finally { setUploading(false); }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Энэ хөрөнгийг устгах уу?')) return;
    try { await api.delete(`/admin/assets/${id}`); load(); loadSummary(); } catch {}
  };

  const [mnt, setMnt] = useState<{ id: string; name: string; date: string; next: string; notes: string } | null>(null);
  const handleMaintenance = (a: any) => setMnt({ id: a.id, name: a.name, date: new Date().toISOString().slice(0, 10), next: '', notes: '' });
  const saveMaintenance = async () => {
    if (!mnt) return;
    try { await api.post(`/admin/assets/${mnt.id}/maintenance`, { maintenance_date: mnt.date, next_maintenance: mnt.next || null, notes: mnt.notes || null }); setMnt(null); load(); loadSummary(); }
    catch (e: any) { alert('Засвар бүртгэхэд алдаа: ' + (e?.message || '')); }
  };

  const openEdit = (a: any) => {
    setEditId(a.id);
    setForm({
      name: a.name || '', category: a.category || 'building', building: a.building || '', location: a.location || '',
      description: a.description || '', purchase_date: a.purchase_date || '',
      purchase_cost: a.purchase_cost ?? '', current_value: a.current_value ?? '',
      serial_number: a.serial_number || '', warranty_expires: a.warranty_expires || '',
      next_maintenance: a.next_maintenance || '', ebarimt_ddtd: a.ebarimt_ddtd || '',
    });
    setEditFiles(a.files || []); setFiles([]); setFormErr('');
    setShowForm(true);
  };

  const openNew = () => { setEditId(null); setForm(emptyForm); setFiles([]); setEditFiles([]); setFormErr(''); setShowForm(true); };

  const formatCurrency = (val: number | null | undefined) => {
    if (val == null) return '-';
    return val.toLocaleString('mn-MN') + '₮';
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 style={{ fontFamily: "'Inter'", fontWeight: 700, fontSize: 24, color: '#1c2b22', margin: 0 }}>
          Хөрөнгийн бүртгэл ({total})
        </h2>
        <button onClick={openNew} style={btnPrimary}>
          + Шинэ хөрөнгө
        </button>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
        <SummaryCard label="Нийт хөрөнгө" value={summary.total_count.toString()} color="#236844" />
        <SummaryCard label="Нийт үнэ цэнэ" value={formatCurrency(summary.total_value)} color="#2f7d52" />
        <SummaryCard label="Засвар хүлээгдэж буй" value={summary.maintenance_due.toString()} color="#f59e0b" />
        <SummaryCard label="Идэвхтэй" value={summary.active_count.toString()} color="#3c9263" />
      </div>

      {/* Category filter */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 12, color: '#5f6f66', marginBottom: 6, fontWeight: 500 }}>Ангилал</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={() => setCategoryFilter('')} style={filterBtn(categoryFilter === '')}>Бүгд</button>
          {Object.entries(CATEGORIES).map(([key, label]) => (
            <button key={key} onClick={() => setCategoryFilter(key)} style={filterBtn(categoryFilter === key)}>{label}</button>
          ))}
        </div>
      </div>

      {/* Status filter */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 12, color: '#5f6f66', marginBottom: 6, fontWeight: 500 }}>Төлөв</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={() => setStatusFilter('')} style={filterBtn(statusFilter === '')}>Бүгд</button>
          {Object.entries(STATUSES).map(([key, label]) => (
            <button key={key} onClick={() => setStatusFilter(key)} style={{
              ...filterBtn(statusFilter === key),
              background: statusFilter === key ? STATUS_COLORS[key] : '#e8e5dc',
            }}>{label}</button>
          ))}
        </div>
      </div>

      {mnt && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setMnt(null)}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 20, width: 380, maxWidth: '92vw' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 12px', color: '#1c2b22' }}>Засвар бүртгэх — {mnt.name}</h3>
            <label style={{ fontSize: 12.5, color: '#5f6f66', fontWeight: 600 }}>Засвар хийсэн огноо</label>
            <input type="date" value={mnt.date} onChange={e => setMnt({ ...mnt, date: e.target.value })} style={{ ...inputStyle, marginBottom: 10 }} />
            <label style={{ fontSize: 12.5, color: '#5f6f66', fontWeight: 600 }}>Дараагийн засварын огноо (заавал биш)</label>
            <input type="date" value={mnt.next} onChange={e => setMnt({ ...mnt, next: e.target.value })} style={{ ...inputStyle, marginBottom: 10 }} />
            <label style={{ fontSize: 12.5, color: '#5f6f66', fontWeight: 600 }}>Тэмдэглэл</label>
            <textarea value={mnt.notes} onChange={e => setMnt({ ...mnt, notes: e.target.value })} rows={3} style={{ ...inputStyle, marginBottom: 14 }} placeholder="Юу засав, хэн хийв, зардал…" />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setMnt(null)} style={smallBtn}>Болих</button>
              <button onClick={saveMaintenance} disabled={!mnt.date} style={btnPrimary}>Хадгалах</button>
            </div>
          </div>
        </div>
      )}

      {/* Asset grid */}
      {loading ? <p style={{ color: '#5f6f66' }}>Уншиж байна...</p> : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16 }}>
          {assets.map(a => (
            <div key={a.id} style={{
              background: '#fff', borderRadius: 12, padding: 20,
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
              borderLeft: `4px solid ${STATUS_COLORS[a.status] || '#6b7280'}`,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <strong style={{ fontSize: 16, color: '#1c2b22', display: 'block', marginBottom: 6 }}>{a.name}</strong>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                    <span style={{
                      fontSize: 11, padding: '2px 8px', borderRadius: 12,
                      background: '#f0f9f4', color: '#236844', fontWeight: 600,
                    }}>
                      {CATEGORIES[a.category] || a.category}
                    </span>
                    <span style={{
                      fontSize: 11, padding: '2px 8px', borderRadius: 12,
                      background: `${STATUS_COLORS[a.status] || '#6b7280'}15`,
                      color: STATUS_COLORS[a.status] || '#6b7280', fontWeight: 600,
                    }}>
                      {STATUSES[a.status] || a.status}
                    </span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <button onClick={() => openEdit(a)} style={smallBtn}>Засах</button>
                  <button onClick={() => handleDelete(a.id)} style={{ ...smallBtn, color: '#ef4444', background: '#fef2f2' }}>Устгах</button>
                </div>
              </div>

              {a.building && (
                <div style={{ fontSize: 13, color: '#5f6f66', marginBottom: 4 }}><span style={{ fontWeight: 500 }}>Байр:</span> {a.building}</div>
              )}
              {a.location && (
                <div style={{ fontSize: 13, color: '#5f6f66', marginBottom: 6 }}>
                  <span style={{ fontWeight: 500 }}>Байршил:</span> {a.location}
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 8 }}>
                <div style={{ fontSize: 12, color: '#5f6f66' }}>
                  <div style={{ fontWeight: 500, marginBottom: 2 }}>Худалдан авсан үнэ</div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: '#1c2b22' }}>{formatCurrency(a.purchase_cost)}</div>
                </div>
                <div style={{ fontSize: 12, color: '#5f6f66' }}>
                  <div style={{ fontWeight: 500, marginBottom: 2 }}>Одоогийн үнэ цэнэ</div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: '#236844' }}>{formatCurrency(a.current_value)}</div>
                </div>
              </div>

              {a.next_maintenance && (
                <div style={{ fontSize: 12, color: '#5f6f66', marginTop: 8 }}>
                  <span style={{ fontWeight: 500 }}>Дараагийн засвар:</span> {a.next_maintenance}
                </div>
              )}

              {a.ebarimt_ddtd && (
                <div style={{ fontSize: 11.5, color: '#5f6f66', marginTop: 8, wordBreak: 'break-all' }}>
                  <span style={{ fontWeight: 500 }}>ДДТД:</span> {fmtDdtd(a.ebarimt_ddtd)}{a.ddtd_source === 'ocr' ? ' ⚠' : ''}
                </div>
              )}
              {(a.files || []).length > 0 && (
                <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                  {a.files.map((f: any) => (
                    <a key={f.id} href={`${API_ORIGIN}${f.url}`} target="_blank" rel="noreferrer" title={f.file_name}
                      style={{ width: 44, height: 44, borderRadius: 6, border: '1px solid #e2e8f0', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', fontSize: 10, color: '#b91c1c', fontWeight: 700, textDecoration: 'none' }}>
                      {f.is_pdf ? 'PDF' : <img src={`${API_ORIGIN}${f.url}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                    </a>
                  ))}
                </div>
              )}

              <div style={{ marginTop: 12, borderTop: '1px solid #f0ede4', paddingTop: 10 }}>
                <button onClick={() => handleMaintenance(a)} style={{
                  background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 6,
                  padding: '5px 12px', cursor: 'pointer', color: '#92400e', fontSize: 12, fontWeight: 500,
                }}>
                  Засвар бүртгэх
                </button>
              </div>
            </div>
          ))}
          {assets.length === 0 && (
            <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: 40, color: '#94a3b8' }}>
              Хөрөнгө олдсонгүй
            </div>
          )}
        </div>
      )}

      {/* Create/Edit modal */}
      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => setShowForm(false)}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 32, width: 560, maxHeight: '85vh', overflow: 'auto' }}
            onClick={e => e.stopPropagation()}>
            <h3 style={{ fontFamily: "'Inter'", fontWeight: 700, fontSize: 20, color: '#1c2b22', marginTop: 0 }}>
              {editId ? 'Хөрөнгө засах' : 'Шинэ хөрөнгө нэмэх'}
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={labelStyle}>Нэр</label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} style={inputStyle} placeholder="Хөрөнгийн нэр" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}>Ангилал</label>
                  <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} style={inputStyle}>
                    {Object.entries(CATEGORIES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Байршил</label>
                  <select value={form.building} onChange={e => setForm({ ...form, building: e.target.value })} style={inputStyle}><option value="">Байр сонгох (заавал биш)</option>{buildings.map(b => <option key={b} value={b}>{b} байр</option>)}{form.building && !buildings.includes(form.building) && <option value={form.building}>{form.building}</option>}</select>
                  <input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} style={inputStyle} placeholder="Байршил (орц, давхар, өрөө)" />
                </div>
              </div>
              <div>
                <label style={labelStyle}>Тайлбар</label>
                <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={3} style={{ ...inputStyle, resize: 'vertical' }} placeholder="Нэмэлт тайлбар" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}>Худалдан авсан огноо</label>
                  <input type="date" value={form.purchase_date} onChange={e => setForm({ ...form, purchase_date: e.target.value })} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Серийн дугаар <span style={{ fontWeight: 400, color: '#8a9a90', fontSize: 12 }}>(хоосон орхивол автомат үүснэ)</span></label>
                  <input value={form.serial_number} onChange={e => setForm({ ...form, serial_number: e.target.value })} style={inputStyle} placeholder="Автомат (жишээ: ELV-00001)" />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}>Худалдан авсан үнэ (₮)</label>
                  <input type="number" value={form.purchase_cost} onChange={e => setForm({ ...form, purchase_cost: e.target.value === '' ? '' : Number(e.target.value) })} style={inputStyle} placeholder="0" />
                </div>
                <div>
                  <label style={labelStyle}>Одоогийн үнэ цэнэ (₮)</label>
                  <input type="number" value={form.current_value} onChange={e => setForm({ ...form, current_value: e.target.value === '' ? '' : Number(e.target.value) })} style={inputStyle} placeholder="0" />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}>Баталгааны хугацаа</label>
                  <input type="date" value={form.warranty_expires} onChange={e => setForm({ ...form, warranty_expires: e.target.value })} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Дараагийн засвар</label>
                  <input type="date" value={form.next_maintenance} onChange={e => setForm({ ...form, next_maintenance: e.target.value })} style={inputStyle} />
                </div>
              </div>
              <div>
                <label style={labelStyle}>ДДТД (е-баримт) <span style={{ fontWeight: 400, color: '#8a9a90', fontSize: 12 }}>(заавал биш — баримтын зургаас автоматаар уншина)</span></label>
                <input value={form.ebarimt_ddtd} onChange={e => setForm({ ...form, ebarimt_ddtd: e.target.value.replace(/\D/g, '').slice(0, 33) })} style={inputStyle} placeholder="33 оронтой дугаар" inputMode="numeric" />
              </div>

              <div>
                <label style={labelStyle}>Баримт хавсаргах <span style={{ fontWeight: 400, color: '#8a9a90', fontSize: 12 }}>(падаан, гэрээ, акт · зураг эсвэл PDF · {MAX_FILES} хүртэл)</span></label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
                  {editFiles.map((f: any) => (
                    <div key={f.id} style={{ position: 'relative', width: 88, height: 88, borderRadius: 8, overflow: 'hidden', border: '1px solid #e2e8f0', background: '#f8fafc' }}>
                      {f.is_pdf
                        ? <a href={`${API_ORIGIN}${f.url}`} target="_blank" rel="noreferrer" style={{ display: 'flex', width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#b91c1c', fontWeight: 700, textDecoration: 'none', textAlign: 'center', padding: 4 }}>PDF<br />{(f.file_name || '').slice(0, 14)}</a>
                        : <a href={`${API_ORIGIN}${f.url}`} target="_blank" rel="noreferrer"><img src={`${API_ORIGIN}${f.url}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /></a>}
                      <button type="button" onClick={() => removeSavedFile(f.id)} aria-label="Устгах" style={{ position: 'absolute', top: 3, right: 3, width: 22, height: 22, borderRadius: '50%', border: 0, background: 'rgba(0,0,0,.6)', color: '#fff', cursor: 'pointer', lineHeight: 1, fontSize: 13 }}>×</button>
                    </div>
                  ))}
                  {files.map((f, i) => (
                    <div key={i} style={{ position: 'relative', width: 88, height: 88, borderRadius: 8, overflow: 'hidden', border: '1px dashed #236844', background: '#f0f9f4' }}>
                      {f.type === 'application/pdf'
                        ? <div style={{ display: 'flex', width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#b91c1c', fontWeight: 700, textAlign: 'center', padding: 4 }}>PDF<br />{f.name.slice(0, 14)}</div>
                        : <img src={URL.createObjectURL(f)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                      <button type="button" onClick={() => setFiles(fs => fs.filter((_, k) => k !== i))} aria-label="Хасах" style={{ position: 'absolute', top: 3, right: 3, width: 22, height: 22, borderRadius: '50%', border: 0, background: 'rgba(0,0,0,.6)', color: '#fff', cursor: 'pointer', lineHeight: 1, fontSize: 13 }}>×</button>
                    </div>
                  ))}
                  {(editFiles.length + files.length) < MAX_FILES && (
                    <button type="button" onClick={() => fileRef.current?.click()} style={{ width: 88, height: 88, borderRadius: 8, border: '1.5px dashed #cbd5e1', background: '#fff', color: '#236844', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>+ Баримт</button>
                  )}
                </div>
                <input ref={fileRef} type="file" accept="image/*,application/pdf" multiple hidden onChange={e => pickFiles(e.target.files)} />
              </div>

              {formErr && <div style={{ background: '#fef2f2', color: '#b91c1c', padding: '8px 12px', borderRadius: 8, fontSize: 13 }}>{formErr}</div>}

              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 4 }}>
                <button onClick={() => setShowForm(false)} style={{ padding: '10px 20px', border: '1px solid #d1d5db', borderRadius: 8, background: '#fff', cursor: 'pointer' }}>Цуцлах</button>
                <button onClick={handleSubmit} disabled={!form.name.trim() || uploading} style={{ ...btnPrimary, opacity: form.name.trim() && !uploading ? 1 : 0.5 }}>
                  {uploading ? 'Хадгалж байна…' : editId ? 'Хадгалах' : 'Нэмэх'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div style={{
      background: '#fff', borderRadius: 12, padding: 20,
      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
      borderTop: `3px solid ${color}`,
    }}>
      <div style={{ fontSize: 13, color: '#5f6f66', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color, fontFamily: "'Inter'" }}>{value}</div>
    </div>
  );
}

function filterBtn(active: boolean): React.CSSProperties {
  return {
    padding: '6px 14px', borderRadius: 20, border: 'none', cursor: 'pointer', fontSize: 13,
    background: active ? '#236844' : '#e8e5dc', color: active ? '#fff' : '#5f6f66', fontWeight: active ? 600 : 400,
  };
}

const btnPrimary: React.CSSProperties = {
  background: 'linear-gradient(180deg, #2f7d52, #236844)', color: '#fff', border: 'none',
  padding: '10px 20px', borderRadius: 10, cursor: 'pointer', fontWeight: 600, fontSize: 14, fontFamily: "'Inter'",
};
const smallBtn: React.CSSProperties = {
  background: '#f0f9f4', border: 'none', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', color: '#236844', fontSize: 12,
};
const labelStyle: React.CSSProperties = { display: 'block', marginBottom: 4, fontSize: 13, color: '#374151', fontWeight: 500 };
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box',
};
